// Le composeur de commentaire, épinglé au pied du dossier : textarea auto-extensible (max ~6
// lignes), Entrée = envoyer, Maj+Entrée = nouvelle ligne. Sur un ticket fermé il reste visible
// mais désactivé, avec l'explication : rouvrir le ticket pour reprendre la discussion.
import React, { useEffect, useRef } from "react";
import { FiLock, FiSend } from "react-icons/fi";

/** Hauteur max du textarea : ~6 lignes de texte sm (leading-relaxed ≈ 20px) + padding vertical. */
const MAX_HEIGHT_PX = 136;

export const TicketComposer: React.FC<{
	value: string;
	onChange: (next: string) => void;
	onSend: () => void;
	sending: boolean;
	/** Ticket fermé → composeur désactivé + indication. */
	closed: boolean;
}> = ({ value, onChange, onSend, sending, closed }) => {
	const ref = useRef<HTMLTextAreaElement | null>(null);

	// Auto-extension : la hauteur suit le contenu jusqu'au plafond, puis le textarea scrolle.
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
		el.style.overflowY = el.scrollHeight > MAX_HEIGHT_PX ? "auto" : "hidden";
	}, [value, closed]);

	if (closed) {
		return (
			<div className="border-t border-slate-100 bg-slate-50/70 px-5 py-3.5">
				<p className="flex items-center gap-2 text-xs text-slate-400">
					<FiLock className="h-3.5 w-3.5 shrink-0" aria-hidden />
					Ce ticket est fermé : rouvrez-le pour reprendre la discussion.
				</p>
			</div>
		);
	}

	return (
		<div className="border-t border-slate-100 bg-white px-5 py-3.5">
			<div className="flex items-end gap-2">
				<textarea
					ref={ref}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							onSend();
						}
					}}
					rows={1}
					placeholder="Ajouter un commentaire…"
					aria-label="Ajouter un commentaire"
					className="max-h-[136px] flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm leading-relaxed text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:bg-white focus:outline-none"
				/>
				<button
					type="button"
					onClick={onSend}
					disabled={!value.trim() || sending}
					className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87] disabled:cursor-not-allowed disabled:opacity-50"
				>
					<FiSend className="h-3.5 w-3.5" /> {sending ? "Envoi…" : "Envoyer"}
				</button>
			</div>
			<p className="mt-1.5 px-1 text-[10px] text-slate-300">Entrée pour envoyer · Maj+Entrée pour une nouvelle ligne</p>
		</div>
	);
};

export default TicketComposer;
